/* eslint-disable max-len */
import { checkRange, toFixedNumber } from '/@/utils';
import { CORN_DENSITY_ANCHOR, CORN_ENVIRONMENT_ANCHOR } from './anchors';

export const CORN_DENSITY_LIMITS_CONFIG = { min: 30000, max: 120000, step: 500 };

/* Helpers */
const getAverageValue = (string) => {
    const values = string.split('-');
    const sum = values.reduce((sum, value) => sum + Number(value), 0);
    return toFixedNumber(sum / (values.length || 1));
};

/* Corn */

export const getCornHarvestDensityCoefficientsForEnvironment = (coefficients, environment) => {
    const filterByEnvironment = checkRange(environment, 'minEnvironment', 'maxEnvironment');
    const coefficientsForEnvironment = coefficients.find(filterByEnvironment);
    if (!coefficientsForEnvironment) return;
    const { minEnvironment, maxEnvironment, ...densityCoefficients } = coefficientsForEnvironment;
    return densityCoefficients;
};

const calculateCornBiologicalHarvestDensity = (coefficients, environment) => {
    const { b1, b2, b4, b5, dm, eiavg } = coefficients;
    const e = environment / CORN_ENVIRONMENT_ANCHOR;
    return (-b1 + 2 * b2 * dm - (b4 * e - b4 * eiavg) - 2 * b5 * dm * (eiavg - e)) / (2 * b2 + 2 * b5 * (e - eiavg));
};

const calculateCornRecommendedHarvestDensity = (coefficients, environment, biologicalDensity) => {
    const { a0, a1, a2 } = coefficients;
    const e = environment / CORN_ENVIRONMENT_ANCHOR;
    return biologicalDensity * ((100 - (a0 * e * e + a1 * e + a2)) / 100);
};

const getDensityForEnvironment = (densities, environment, type = 'recommended') => {
    const density = densities
        .filter(checkRange(environment, 'minEnvironment', 'maxEnvironment'))
        .find((density) => density.type === type);
    if (!density) return;
    return density;
};

export const getCornHarvestDensity = (profile, coefficients, environment, userDensity) => {
    const densities = profile.densities.filter((density) => density.minEnvironment || density.maxEnvironment);
    if (densities.length) {
        const biological = getDensityForEnvironment(densities, environment, 'biological');
        const recommended = getDensityForEnvironment(densities, environment, 'recommended');
        return {
            user: userDensity,
            recommended: recommended && recommended.value * CORN_DENSITY_ANCHOR,
            minEnvironmentForRecommended: recommended && recommended.minEnvironment,
            maxEnvironmentForRecommended: recommended && recommended.maxEnvironment,
            biological: biological && biological.value * CORN_DENSITY_ANCHOR,
            minEnvironmentForBiological: biological && biological.minEnvironment,
            maxEnvironmentForBiological: biological && biological.maxEnvironment,
        };
    }

    if (coefficients) {
        const biologicalDensity = calculateCornBiologicalHarvestDensity(coefficients, environment);
        const recommendedDensity = calculateCornRecommendedHarvestDensity(coefficients, environment, biologicalDensity);
        return {
            user: userDensity,
            biological: biologicalDensity * CORN_DENSITY_ANCHOR,
            recommended: recommendedDensity * CORN_DENSITY_ANCHOR,
        };
    }
};

export const getCornPlantingDensity = (harvestDensity, planterEfficiency, seedsPerBag) => {
    if (!harvestDensity || !planterEfficiency) return {};
    const density = (harvestDensity / planterEfficiency) * 100;
    const bagsPerHectare = seedsPerBag ? density / seedsPerBag : undefined;
    return { planterEfficiency, density, bagsPerHectare };
};

/* Soy */
export const getSoyHarvestDensity = (densities, environment) => {
    const density = densities.find(checkRange(environment, 'minEnvironment', 'maxEnvironment'));
    return (
        density && {
            recommended: density.value,
            average: getAverageValue(density.value),
        }
    );
};

export const getSoyPlantingDensity = (harvestDensity, planterEfficiency, germinatingPower, p1000) => {
    const purity = 0.98;
    const culturalValue = (germinatingPower / 100) * purity;
    const density = harvestDensity / culturalValue / (planterEfficiency / 100);
    const kgPerHectare = density * (p1000 / 1000000);
    return { planterEfficiency, density, kgPerHectare };
};
